import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Loader2, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { lang } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/app/kreation');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setIsSubmitting(true);

    try {
      await login(trimmedEmail, trimmedPassword);
      toast.success(lang === 'de' ? 'Erfolgreich angemeldet' : 'Successfully logged in');
      navigate('/app/kreation');
    } catch (err) {
      const message = err.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b border-[#1A2B42]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
              alt="xchannels.ai" 
              className="h-12"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <h1 className="text-4xl font-black tracking-tight mb-3 text-white">
            {lang === 'de' ? 'Willkommen zurück' : 'Welcome back'}
          </h1>
          <p className="text-[#A0AEC0] mb-8">
            {lang === 'de' ? 'Melde dich an, um fortzufahren.' : 'Log in to continue.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{lang === 'de' ? 'E-Mail' : 'Email'}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6C7D]" />
                <Input
                  id="email"
                  type="email"
                  placeholder={lang === 'de' ? 'deine@email.de' : 'your@email.com'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D]"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">{lang === 'de' ? 'Passwort' : 'Password'}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6C7D]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={lang === 'de' ? 'Dein Passwort' : 'Your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D]"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPassword(!showPassword);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A6C7D] hover:text-white transition-colors z-10"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-400" data-testid="login-error">
                {error}
              </p>
            )}

            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {lang === 'de' ? 'Wird angemeldet...' : 'Logging in...'}
                </>
              ) : (
                <>
                  {lang === 'de' ? 'Anmelden' : 'Log in'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#A0AEC0]">
            {lang === 'de' ? 'Noch kein Account?' : "Don't have an account?"}{' '}
            <Link 
              to="/demo" 
              className="text-[#00D9C8] hover:underline"
              data-testid="signup-link"
            >
              {lang === 'de' ? 'Jetzt starten' : 'Get started'}
            </Link>
          </p>

          {/* Legal Links */}
          <div className="mt-8 pt-6 border-t border-[#1A2B42] flex justify-center gap-4 text-xs text-[#5A6C7D]">
            <Link to="/impressum" className="hover:text-[#A0AEC0]" data-testid="impressum-link">
              {lang === 'de' ? 'Impressum' : 'Legal Notice'}
            </Link>
            <span>|</span>
            <Link to="/datenschutz" className="hover:text-[#A0AEC0]" data-testid="datenschutz-link">
              {lang === 'de' ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Login;
